import chokidar from "chokidar";
import debounce from "lodash.debounce";
import path from "path";
import fs from "fs-extra";
import http from "http";
import https from "https";
import type { AddressInfo } from "net";

import { buildMockIndex, saveMockData } from "./mockFileUtils";
import { createInspectorHandler, normalizeInspectorConfig } from "./inspector";
import {
  resolveAbsolutePath,
  getServerAddress,
  sendError,
} from "./utils";

import type { Plugin } from "vite";
import type { IncomingMessage, ServerResponse } from "http";
import type { FSWatcher } from "chokidar";
import type { AutomockPluginOptions } from "./types";

function serveMockResponse(
  res: ServerResponse,
  mockFilePath: string,
  mockResult: { data?: unknown; delay?: number; status?: number },
): void {
  const { data, delay = 0, status = 200 } = mockResult;

  setTimeout(() => {
    const isBinaryMock = data && typeof data === "object" && (data as Record<string, unknown>).__binaryFile;

    if (isBinaryMock) {
      const binaryData = data as Record<string, unknown>;
      const binaryFilePath = mockFilePath.replace(
        /\.js$/,
        "." + binaryData.__binaryFile,
      );

      if (!fs.existsSync(binaryFilePath)) {
        console.error(
          `[automock] Binary mock file not found: ${binaryFilePath}`,
        );
        sendError(res, "Binary mock file not found", 404);
        return;
      }

      try {
        const fileData = fs.readFileSync(binaryFilePath);
        const contentType =
          (binaryData.__contentType as string) || "application/octet-stream";
        res.setHeader("Content-Type", contentType);
        res.setHeader("Content-Length", fileData.length);
        res.setHeader("X-Mock-Response", "true");
        res.setHeader("X-Mock-Source", "vite-plugin-automock");
        res.setHeader("X-Mock-Binary-File", "true");
        res.statusCode = status;
        res.end(fileData);
      } catch (error) {
        console.error("[automock] Failed to read binary mock file:", error);
        sendError(res, "Failed to read binary mock file");
      }
    } else {
      res.setHeader("Content-Type", "application/json; charset=utf-8");
      res.setHeader("X-Mock-Response", "true");
      res.setHeader("X-Mock-Source", "vite-plugin-automock");
      res.statusCode = status;
      res.end(typeof data === "string" ? data : JSON.stringify(data));
    }
  }, delay);
}

function matchesApiPrefix(pathname: string, apiPrefix: string): boolean {
  if (!apiPrefix || apiPrefix === "/") return true;
  const normalizedPrefix =
    apiPrefix.endsWith("/") && apiPrefix !== "/"
      ? apiPrefix.slice(0, -1)
      : apiPrefix;

  return pathname === normalizedPrefix || pathname.startsWith(`${normalizedPrefix}/`);
}

function proxyAndCapture(
  req: IncomingMessage,
  res: ServerResponse,
  options: {
    proxyBaseUrl: string;
    pathRewrite: (p: string) => string;
    shouldSave: boolean;
    method: string;
    pathname: string;
    mockDir: string;
    onMockSaved: () => void;
  },
): void {
  const { proxyBaseUrl, pathRewrite, shouldSave, method, pathname, mockDir, onMockSaved } = options;
  const targetUrl = proxyBaseUrl + pathRewrite(req.url || "");
  const client = targetUrl.startsWith("https") ? https : http;

  function sendProxyRequest(body?: string) {
    const targetUrlObj = new URL(proxyBaseUrl);
    const proxyOptions = {
      method: req.method,
      headers: {
        ...req.headers,
        host: targetUrlObj.host,
      },
      rejectUnauthorized: false,
    };

    const proxyReq = client.request(targetUrl, proxyOptions, (proxyRes) => {
      const contentType = proxyRes.headers["content-type"] || "";
      const isJson = contentType.includes("application/json");

      // Non-JSON response (file download, stream, etc.) → pipe directly
      if (!isJson) {
        res.writeHead(proxyRes.statusCode || 200, proxyRes.headers);
        proxyRes.pipe(res);
        return;
      }

      // JSON response → buffer, save mock, then respond
      const chunks: Buffer[] = [];

      proxyRes.on("data", (chunk) => chunks.push(chunk));
      proxyRes.on("end", async () => {
        try {
          const responseData = Buffer.concat(chunks);

          if (shouldSave) {
            try {
              console.log(
                `[automock] Capturing mock: ${req.url!} -> ${pathname}`,
              );
              const savedFilePath = await saveMockData(
                req.url!,
                method,
                responseData,
                mockDir,
                proxyRes.statusCode,
                contentType,
              );
              if (savedFilePath) {
                console.log(`[automock] Mock saved: ${pathname}`);
                onMockSaved();
              } else {
                console.log(
                  `[automock] Mock file already exists, skipped: ${pathname}`,
                );
              }
            } catch (saveError) {
              console.error("[automock] Failed to save mock:", saveError);
            }
          }

          res.writeHead(proxyRes.statusCode || 200, proxyRes.headers);
          res.end(responseData);
        } catch (error) {
          console.error("[automock] Failed to process response:", error);
          res.writeHead(proxyRes.statusCode || 200, proxyRes.headers);
          res.end(Buffer.concat(chunks));
        }
      });
    });

    proxyReq.on("error", (err) => {
      console.error("[automock] Proxy request failed:", err);
      sendError(res, err.message);
    });

    if (
      body &&
      (req.method === "POST" || req.method === "PUT" || req.method === "PATCH")
    ) {
      proxyReq.write(body);
    }

    proxyReq.end();
  }

  if (
    req.method === "POST" ||
    req.method === "PUT" ||
    req.method === "PATCH"
  ) {
    let bodyStr = "";
    req.on("data", (chunk) => (bodyStr += chunk.toString()));
    req.on("end", () => sendProxyRequest(bodyStr));
  } else {
    sendProxyRequest();
  }
}

function printServerUrls(
  server: { httpServer?: { address(): AddressInfo | string | null; once(event: string, cb: () => void): void } | null; config: { server: { https?: unknown } } },
  urls: Array<{ path: string; label: string; color: string; delay: number }>,
): void {
  server.httpServer?.once("listening", () => {
    const address = server.httpServer?.address();
    if (!address || typeof address !== "object") return;

    const { protocol, host, port } = getServerAddress(
      address,
      !!server.config.server.https,
    );

    for (const { path: urlPath, label, color, delay } of urls) {
      setTimeout(() => {
        const fullUrl = `${protocol}://${host}:${port}${urlPath}`;
        console.log(`  ➜  ${color}${label}\x1b[0m: \x1b[1m${fullUrl}\x1b[0m`);
      }, delay);
    }
  });
}

export function automock(options: AutomockPluginOptions): Plugin {
  const {
    mockDir: configMockDir = path.join(process.cwd(), "mock"),
    apiPrefix = "/api",
    pathRewrite = (p) => p,
    proxyBaseUrl,
    inspector = false,
    enabled = true,
    proxy: proxyEnabled = true,
    capture: captureEnabled = true,
  } = options;

  const mockDir = resolveAbsolutePath(configMockDir);

  if (!fs.existsSync(mockDir)) {
    try {
      fs.ensureDirSync(mockDir);
      console.log(`[automock] Mock directory created: ${mockDir}`);
    } catch (error) {
      console.error(`[automock] Failed to create mock directory: ${mockDir}`, error);
    }
  }

  let watcher: FSWatcher | undefined;

  return {
    name: "vite-plugin-automock",
    config(userConfig) {
      if (proxyBaseUrl && userConfig.server?.proxy) {
        const proxyConfig = userConfig.server.proxy;
        const hasConflictingPrefix =
          typeof proxyConfig === "object" && apiPrefix in proxyConfig;

        if (hasConflictingPrefix) {
          console.warn(
            `\n⚠️  [automock] WARNING: You have both Vite's server.proxy["${apiPrefix}"] and automock's proxyBaseUrl configured.`,
          );
          console.warn(
            `   This may cause request conflicts. Remove the "${apiPrefix}" entry from vite.config.ts server.proxy to avoid issues.`,
          );
          console.warn(
            `   Automock will handle all ${apiPrefix} requests when proxyBaseUrl is set.\n`,
          );
        }
      }
    },
    async configureServer(server) {
      let mockFileMap = await buildMockIndex(mockDir);
      console.log(`[automock] Loaded ${mockFileMap.size} mock files`);

      const rebuildMockFileMap = debounce(async () => {
        mockFileMap = await buildMockIndex(mockDir);
      }, 200);

      watcher = chokidar.watch(mockDir, {
        ignoreInitial: true,
        persistent: true,
        depth: 30,
      });
      watcher.on("add", () => rebuildMockFileMap());
      watcher.on("unlink", () => rebuildMockFileMap());

      server.httpServer?.on("close", () => watcher?.close());

      const inspectorHandler = createInspectorHandler({
        inspector,
        apiPrefix,
        mockDir,
        getMockFileMap: () => mockFileMap,
      });

      const urlsToPrint: Array<{ path: string; label: string; color: string; delay: number }> = [
        { path: apiPrefix, label: "Mock API", color: "\x1b[32m", delay: 100 },
      ];

      if (inspector) {
        const inspectorConfig = normalizeInspectorConfig(inspector);
        urlsToPrint.push({
          path: inspectorConfig.route,
          label: "Mock Inspector",
          color: "\x1b[95m",
          delay: 150,
        });
      }

      printServerUrls(server, urlsToPrint);

      async function tryLoadMock(key: string): Promise<{ absolutePath: string; mockResult: Record<string, unknown> } | null> {
        const mockFilePath = mockFileMap.get(key);
        if (!mockFilePath) return null;

        const absolutePath = resolveAbsolutePath(mockFilePath);
        if (!fs.existsSync(absolutePath)) return null;

        const { default: mockModule } = await import(
          `${absolutePath}?t=${Date.now()}`
        );
        const exportedConfig =
          typeof mockModule === "function" ? await mockModule() : mockModule;
        const mockResult =
          typeof exportedConfig?.data === "function"
            ? { ...exportedConfig, data: await exportedConfig.data() }
            : exportedConfig;

        const { enable = true } = mockResult || {};
        return enable ? { absolutePath, mockResult } : null;
      }

      server.middlewares.use(
        async (
          req: IncomingMessage,
          res: ServerResponse,
          next: (err?: Error | unknown) => void,
        ): Promise<void> => {
          // Global switch: skip all mock handling
          if (!enabled) return next();

          if (inspectorHandler && req.url) {
            const handled = await inspectorHandler(req, res);
            if (handled) return;
          }

          if (!req.url) {
            return next();
          }

          const pathname = new URL(req.url, "http://localhost").pathname;
          if (!matchesApiPrefix(pathname, apiPrefix)) {
            return next();
          }

          // Skip non-standard requests (SSE, WebSocket, file streams)
          const accept = req.headers.accept || "";
          const upgrade = req.headers.upgrade || "";
          if (accept.includes("text/event-stream") || upgrade.toLowerCase() === "websocket") {
            return next();
          }

          const method = (req.method || "GET").toLowerCase();
          const key = `${pathname}/${method}.js`.toLowerCase();

          // 1. Try mock
          try {
            const mock = await tryLoadMock(key);
            if (mock) {
              serveMockResponse(res, mock.absolutePath, mock.mockResult);
              return;
            }
          } catch (error) {
            console.error("[automock] Failed to load mock file:", error);
          }

          // 2. Try proxy
          if (!proxyBaseUrl || !proxyEnabled) return next();

          try {
            proxyAndCapture(req, res, {
              proxyBaseUrl,
              pathRewrite,
              shouldSave: captureEnabled && !mockFileMap.has(key),
              method,
              pathname,
              mockDir,
              onMockSaved: async () => {
                mockFileMap = await buildMockIndex(mockDir);
              },
            });
          } catch (error) {
            console.error("[automock] Proxy request error:", error);
            sendError(res, "Internal server error");
          }
        },
      );
    },
    closeBundle() {
      watcher?.close();
    },
    transform() {
      return null;
    },
  };
}
